import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/PageHeader';

const pricingTiers = [
  {
    name: 'Starter',
    price: '49',
    features: [
      'Access to Basic AI Models',
      '100 API Calls per Month',
      'Standard Support',
      'Basic Analytics',
      'Single User License'
    ],
    cta: 'Start Free Trial',
    highlighted: false
  },
  {
    name: 'Professional',
    price: '99',
    features: [
      'Access to All AI Models',
      '1000 API Calls per Month',
      'Priority Support',
      'Advanced Analytics',
      'Team Collaboration (up to 5)',
      'Custom Model Training'
    ],
    cta: 'Upgrade Now',
    highlighted: true
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    features: [
      'Unlimited API Calls',
      'Dedicated Support',
      'Enterprise Analytics',
      'Unlimited Team Size',
      'Custom Model Development',
      'SLA Guarantees',
      'On-Premise Deployment'
    ],
    cta: 'Contact Sales',
    highlighted: false
  }
];

export function Subscriptions() {
  const { currentUser } = useAuth();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Subscription Management"
        subtitle="Scale your AI capabilities with our flexible pricing options"
      />

      {/* Current Subscription Status */}
      <div className="mt-8 p-4 bg-primary-50 rounded-lg border border-primary-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-primary-900">Current Plan</h2>
            <p className="text-primary-700">Professional Plan - Active</p>
          </div>
          <div className="text-sm text-primary-700">
            Next billing date: {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="mt-12 grid gap-8 lg:grid-cols-3">
        {pricingTiers.map((tier) => (
          <div
            key={tier.name}
            className={`
              relative rounded-2xl border p-8 shadow-sm flex flex-col
              ${tier.highlighted 
                ? 'border-primary-200 bg-primary-50 shadow-primary-100' 
                : 'border-gray-200 bg-white'
              }
            `}
          >
            {tier.highlighted && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{tier.name}</h3>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-bold tracking-tight text-gray-900">
                  ${tier.price}
                </span>
                {tier.price !== 'Custom' && (
                  <span className="ml-1 text-xl font-semibold text-gray-600">/month</span>
                )}
              </div>
            </div>

            <ul className="mb-6 space-y-4 flex-1">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-center">
                  <svg
                    className="h-5 w-5 text-primary-500 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              className={`
                w-full rounded-lg px-4 py-2 text-center text-sm font-semibold
                ${tier.highlighted
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-white text-primary-600 border border-primary-600 hover:bg-primary-50'
                }
                transition-colors duration-200
              `}
            >
              {tier.cta}
            </button>
          </div>
        ))}
      </div>

      {/* FAQ Section */}
      <div className="mt-16">
        <h2 className="text-2xl font-bold text-gray-900 text-center">
          Frequently Asked Questions
        </h2>
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900">
              Can I change my plan later?
            </h3>
            <p className="mt-2 text-gray-600">
              Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900">
              What payment methods do you accept?
            </h3>
            <p className="mt-2 text-gray-600">
              We accept all major credit cards, PayPal, and wire transfers for Enterprise plans.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 